import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  AdminRole,
  AdminUser,
  adminUsersApi,
} from '../../../../api/adminUsersApi';
import {
  colors,
  primaryButtonSx,
  tableCellSx,
  tableHeadCellSx,
  typo,
} from '../../../../theme/tokens';

import AddUserInlineRow from './AddUserInlineRow';
import PendingFirstLoginChip from './PendingFirstLoginChip';
import LastSuperAdminTooltip from './LastSuperAdminTooltip';
import InlineEditCell from './InlineEditCell';
import { WriteButton, WriteIconButton } from '../../../../components/ui';
import AccessGrantsDrawer from './AccessGrantsDrawer';

interface Props {
  users: AdminUser[];
  roles: AdminRole[];
  onNotify: (msg: string, sev: 'success' | 'error' | 'info') => void;
}

function initialsFor(email: string | null | undefined): string {
  if (!email) return '?';
  const local = email.split('@')[0] || email;
  const parts = local.split(/[._-]/).filter(Boolean);
  const picks = parts.length >= 2 ? [parts[0][0], parts[1][0]] : [local[0]];
  return picks.join('').toUpperCase().slice(0, 2);
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diffSec = Math.round((Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  return `${mo}mo ago`;
}

const UsersTable: React.FC<Props> = ({ users, roles, onNotify }) => {
  const queryClient = useQueryClient();
  // Portal-user management (config.app_user via /api/admin/users) is env-local
  // operational data, editable on read-only envs (see the read-only-relax work).
  // So the per-user role dropdown + active toggle are NOT env-read-only-gated —
  // only the super-admin invariant (isLastSuperAdmin) and in-flight state gate them.
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    user: AdminUser;
  } | null>(null);
  const [accessUser, setAccessUser] = useState<AdminUser | null>(null);

  /**
   * Pre-empt the backend guardrail: if only 1 user is both active AND
   * holds the super-admin role, disable deactivate + role-change on that
   * specific user. Backend still enforces the invariant — this is
   * advisory UI.
   */
  const superAdminActiveCount = useMemo(
    () => users.filter((u) => (u.roles ?? []).includes('super-admin') && u.active).length,
    [users],
  );

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(
      (u) =>
        u.email.toLowerCase().includes(needle) ||
        (u.display_name ?? '').toLowerCase().includes(needle),
    );
  }, [users, search]);

  const updateMutation = useMutation({
    mutationFn: (args: {
      userId: number;
      patch: { role_code?: string; active?: boolean; display_name?: string | null; email?: string };
    }) => adminUsersApi.updateUser(args.userId, args.patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'last_super_admin') {
        onNotify('Cannot remove the last super-admin.', 'error');
      } else if (code === 'email_exists') {
        onNotify('That email is already in use by another user.', 'error');
      } else if (code === 'invalid_email') {
        onNotify('Enter a valid email address.', 'error');
      } else if (code === 'invalid_display_name') {
        onNotify('Display name is too long (max 100 characters).', 'error');
      } else {
        onNotify('Update failed.', 'error');
      }
    },
  });

  const rolesMutation = useMutation({
    mutationFn: (args: { userId: number; roles: string[] }) =>
      adminUsersApi.updateRoles(args.userId, args.roles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'last_super_admin') {
        onNotify('Cannot remove the last super-admin.', 'error');
      } else if (code === 'cannot_self_demote') {
        onNotify('You cannot remove your own super-admin role.', 'error');
      } else if (code === 'invalid_roles') {
        onNotify('Select at least one role.', 'error');
      } else if (code === 'role_not_found') {
        onNotify('One of the selected roles no longer exists.', 'error');
      } else {
        onNotify('Role update failed.', 'error');
      }
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => adminUsersApi.removeUser(userId),
    onSuccess: () => {
      onNotify('User removed.', 'success');
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
    },
    onError: (err: any) => {
      const code = err?.response?.data?.error;
      if (code === 'last_super_admin') {
        onNotify('Cannot remove the last super-admin.', 'error');
      } else if (code === 'has_activity') {
        onNotify('User has activity — deactivate instead of removing.', 'error');
      } else {
        onNotify('Remove failed.', 'error');
      }
    },
  });

  const handleMenuClose = () => setMenuAnchor(null);

  const closeAndRun = (fn: () => void) => {
    handleMenuClose();
    fn();
  };

  const copyEmail = (email: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(email).then(
        () => onNotify('Email copied.', 'success'),
        () => onNotify('Copy failed.', 'error'),
      );
    } else {
      onNotify('Clipboard unavailable.', 'error');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <TextField
          size="small"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 320, flex: '1 1 320px' }}
        />
        {!showAdd && (
          <WriteButton ignoreEnvReadOnly
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAdd(true)}
            sx={primaryButtonSx}
          >
            Invite user
          </WriteButton>
        )}
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: '12px',
          border: `1px solid ${colors.borderLight}`,
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableHeadCellSx}>User</TableCell>
                <TableCell sx={tableHeadCellSx}>Display name</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 200 }}>Role</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 96 }}>Active</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 160 }}>Last login</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: 64, textAlign: 'center' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && !showAdd && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography sx={{ ...typo.secondaryText }}>
                      No users match the current search.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {filtered.map((u) => {
                const isLastSuperAdmin =
                  (u.roles ?? []).includes('super-admin') && superAdminActiveCount <= 1 && u.active;
                return (
                  <TableRow key={u.user_id} hover>
                    <TableCell sx={tableCellSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: 12,
                            fontWeight: 700,
                            backgroundColor: colors.adminAccent,
                            color: colors.bgWhite,
                          }}
                        >
                          {initialsFor(u.email)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <InlineEditCell
                            value={u.email}
                            mono
                            label="email"
                            disabled={updateMutation.isPending}
                            confirmMessage={
                              u.first_login_ts != null
                                ? 'This user has already logged in. Changing their email changes how they are identified — they will sign in with the new address. Continue?'
                                : undefined
                            }
                            onSave={(next) =>
                              updateMutation.mutate({ userId: u.user_id, patch: { email: next } })
                            }
                          />
                          {u.first_login_ts == null && (
                            <Box sx={{ mt: 0.5 }}>
                              <PendingFirstLoginChip />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <InlineEditCell
                        value={u.display_name ?? ''}
                        label="display name"
                        disabled={updateMutation.isPending}
                        onSave={(next) =>
                          updateMutation.mutate({
                            userId: u.user_id,
                            patch: { display_name: next.length ? next : null },
                          })
                        }
                      />
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <LastSuperAdminTooltip
                        superAdminCount={
                          u.role_code === 'super-admin' ? superAdminActiveCount : 99
                        }
                        action="demote"
                      >
                        <TextField
                          size="small"
                          select
                          value={u.roles ?? []}
                          onChange={(e) =>
                            rolesMutation.mutate({
                              userId: u.user_id,
                              // MUI multi-select gives back an array of codes.
                              roles: e.target.value as unknown as string[],
                            })
                          }
                          disabled={isLastSuperAdmin || rolesMutation.isPending}
                          SelectProps={{
                            multiple: true,
                            renderValue: (selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(selected as string[]).map((code) => (
                                  <Chip
                                    key={code}
                                    size="small"
                                    label={roles.find((r) => r.role_code === code)?.role_name ?? code}
                                  />
                                ))}
                              </Box>
                            ),
                          }}
                          sx={{ minWidth: 200 }}
                        >
                          {roles.map((r) => (
                            <MenuItem key={r.role_code} value={r.role_code}>
                              <Checkbox size="small" checked={(u.roles ?? []).includes(r.role_code)} />
                              <ListItemText primary={r.role_name} />
                            </MenuItem>
                          ))}
                        </TextField>
                      </LastSuperAdminTooltip>
                    </TableCell>
                    <TableCell sx={tableCellSx}>
                      <LastSuperAdminTooltip
                        superAdminCount={
                          u.role_code === 'super-admin' ? superAdminActiveCount : 99
                        }
                        action="deactivate"
                      >
                        <Switch
                          size="small"
                          checked={u.active}
                          disabled={isLastSuperAdmin || updateMutation.isPending}
                          onChange={(e) =>
                            updateMutation.mutate({
                              userId: u.user_id,
                              patch: { active: e.target.checked },
                            })
                          }
                        />
                      </LastSuperAdminTooltip>
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, color: colors.textSecondary }}>
                      {formatRelative(u.last_login_at)}
                    </TableCell>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'center' }}>
                      <WriteIconButton ignoreEnvReadOnly
                        size="small"
                        onClick={(e) => setMenuAnchor({ el: e.currentTarget, user: u })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </WriteIconButton>
                    </TableCell>
                  </TableRow>
                );
              })}

              {showAdd && (
                <AddUserInlineRow
                  roles={roles}
                  onCancel={() => setShowAdd(false)}
                  onCreated={(row) => {
                    setShowAdd(false);
                    onNotify(`Invited ${row.email}`, 'success');
                    queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
                    queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
                  }}
                  onError={(msg) => onNotify(msg, 'error')}
                />
              )}

              {!showAdd && users.length > 0 && (
                <TableRow
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setShowAdd(true)}
                  data-write-action="true"
                >
                  <TableCell colSpan={6} sx={{ ...tableCellSx, py: 1, textAlign: 'center' }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        color: colors.adminAccent,
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      <AddIcon fontSize="small" /> Invite user
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Menu
        anchorEl={menuAnchor?.el ?? null}
        open={!!menuAnchor}
        onClose={handleMenuClose}
      >
        {menuAnchor && (
          <>
            {menuAnchor.user.active ? (
              <MenuItem
                data-write-action="true"
                onClick={() =>
                  closeAndRun(() =>
                    updateMutation.mutate({
                      userId: menuAnchor.user.user_id,
                      patch: { active: false },
                    }),
                  )
                }
                disabled={
                  menuAnchor.user.role_code === 'super-admin' && superAdminActiveCount <= 1
                }
              >
                Deactivate
              </MenuItem>
            ) : (
              <MenuItem
                data-write-action="true"
                onClick={() =>
                  closeAndRun(() =>
                    updateMutation.mutate({
                      userId: menuAnchor.user.user_id,
                      patch: { active: true },
                    }),
                  )
                }
              >
                Reactivate
              </MenuItem>
            )}
            <MenuItem
              data-write-action="true"
              onClick={() => closeAndRun(() => setAccessUser(menuAnchor.user))}
            >
              Manage tenant access
            </MenuItem>
            <MenuItem
              data-write-action="true"
              onClick={() =>
                closeAndRun(() => {
                  if (
                    window.confirm(
                      `Remove ${menuAnchor.user.email}? This is only allowed for users who never logged in.`,
                    )
                  )
                    removeMutation.mutate(menuAnchor.user.user_id);
                })
              }
              disabled={menuAnchor.user.first_login_ts != null}
              sx={{ color: colors.errorText }}
            >
              Remove…
            </MenuItem>
            <MenuItem onClick={() => closeAndRun(() => copyEmail(menuAnchor.user.email))}>
              <ContentCopyIcon fontSize="small" sx={{ mr: 1, color: colors.textSecondary }} />
              Copy email
            </MenuItem>
          </>
        )}
      </Menu>

      <AccessGrantsDrawer
        open={!!accessUser}
        user={accessUser}
        onClose={() => setAccessUser(null)}
        onNotify={onNotify}
      />
    </Box>
  );
};

export default UsersTable;
